import { LoggerLevelEnum } from "@/enums";
import type { AppErrorOptions, AppErrorSeverity, UnknownRecord } from "@/types";

export const APP_ERROR_NAME = "AppError";
export const APP_ERROR_DEFAULT_CODE = "APP_ERROR";

export class AppError<M extends UnknownRecord = UnknownRecord> extends Error {
  readonly code: string;
  readonly severity: AppErrorSeverity;
  readonly status?: number;
  readonly meta?: M;
  constructor(message: string, opts?: AppErrorOptions<M>);
  constructor(code: string, message: string, opts?: AppErrorOptions<M>);
  constructor(
    a: string,
    b?: string | AppErrorOptions<M>,
    c?: AppErrorOptions<M>,
  ) {
    let code: string;
    let message: string;
    let opts: AppErrorOptions<M> | undefined;

    if (typeof b === "string") {
      code = a;
      message = b;
      opts = c;
    } else {
      code =
        (b as AppErrorOptions<M> | undefined)?.code ?? APP_ERROR_DEFAULT_CODE;
      message = a;
      opts = b as AppErrorOptions<M> | undefined;
    }

    super(message, { cause: opts?.cause });
    this.name = APP_ERROR_NAME;
    this.code = code;
    this.severity = opts?.severity ?? LoggerLevelEnum.ERROR;
    this.status = opts?.status;
    this.meta = opts?.meta;
  }
}

export function isAppError(x: unknown): x is AppError {
  if (x instanceof AppError) return true;
  if (!x || typeof x !== "object") return false;
  const name = (x as { name?: unknown }).name;
  return name === APP_ERROR_NAME;
}

export function createAppError<M extends UnknownRecord = UnknownRecord>(
  message: string,
  opts?: AppErrorOptions<M>,
): AppError<M>;
export function createAppError<M extends UnknownRecord = UnknownRecord>(
  code: string,
  message: string,
  opts?: AppErrorOptions<M>,
): AppError<M>;
export function createAppError<M extends UnknownRecord = UnknownRecord>(args: {
  code?: string;
  message: string;
  severity?: AppErrorSeverity;
  status?: number;
  cause?: unknown;
  meta?: M;
}): AppError<M>;
export function createAppError<M extends UnknownRecord = UnknownRecord>(
  a:
    | string
    | {
        code?: string;
        message: string;
        severity?: AppErrorSeverity;
        status?: number;
        cause?: unknown;
        meta?: M;
      },
  b?: string | AppErrorOptions<M>,
  c?: AppErrorOptions<M>,
): AppError<M> {
  if (typeof a === "string") {
    if (typeof b === "string") {
      return new AppError<M>(a, b, c);
    }
    return new AppError<M>(a, b);
  }
  const { code, message, ...rest } = a;
  return new AppError<M>(code ?? APP_ERROR_DEFAULT_CODE, message, rest);
}
